// src/components/Groups/GroupSettingsPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Save, Trash2, Upload, Globe, Lock, EyeOff,
  Check, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { groupsAPI } from '../../api/groupsApi';
import './GroupSettingsPanel.css';

const VISIBILITY_OPTIONS = [
  { value: 'public',  icon: Globe,  label: 'Public',  desc: 'Anyone can discover and join' },
  { value: 'private', icon: Lock,   label: 'Private', desc: 'Visible but invite-only' },
  { value: 'secret',  icon: EyeOff, label: 'Secret',  desc: 'Hidden from non-members' },
];

const DEFAULT_ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer', desc: 'Can only view shared resources' },
  { value: 'editor', label: 'Editor', desc: 'Can view and edit shared resources' },
];

const GroupSettingsPanel = ({ group, isOwner, onSaved, onDeleted, onError }) => {
  // ── Form state ────────────────────────────────────────────────────────────
  const [name,              setName]              = useState(group.name);
  const [description,       setDescription]       = useState(group.description || '');
  const [visibility,        setVisibility]        = useState(group.visibility);
  const [defaultMemberRole, setDefaultMemberRole] = useState(group.default_member_role || 'viewer');
  const [allowMemberInvite, setAllowMemberInvite] = useState(group.allow_member_invite);
  const [requireApproval,   setRequireApproval]   = useState(group.require_invite_approval);
  const [maxMembers,        setMaxMembers]        = useState(group.max_members ?? 50);
  const [avatarFile,        setAvatarFile]        = useState(null);
  const [avatarPreview,     setAvatarPreview]     = useState(group.avatar || null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving,           setSaving]           = useState(false);
  const [deleting,         setDeleting]         = useState(false);
  const [confirmDelete,    setConfirmDelete]     = useState(false);
  const [deleteInput,      setDeleteInput]      = useState('');
  const [localError,       setLocalError]       = useState('');
  const fileRef = useRef();

  // Keep form in sync if parent refreshes group
  useEffect(() => {
    setName(group.name);
    setDescription(group.description || '');
    setVisibility(group.visibility);
    setDefaultMemberRole(group.default_member_role || 'viewer');
    setAllowMemberInvite(group.allow_member_invite);
    setRequireApproval(group.require_invite_approval);
    setMaxMembers(group.max_members ?? 50);
    setAvatarPreview(group.avatar || null);
    setAvatarFile(null);
  }, [group]);

  const handleVisibilityChange = (val) => {
    setVisibility(val);
    if (val !== 'public') setDefaultMemberRole('viewer');
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const isDirty = () =>
    name              !== group.name              ||
    description       !== (group.description || '') ||
    visibility        !== group.visibility        ||
    defaultMemberRole !== (group.default_member_role || 'viewer') ||
    allowMemberInvite !== group.allow_member_invite ||
    requireApproval   !== group.require_invite_approval ||
    maxMembers        !== (group.max_members ?? 50) ||
    avatarFile        !== null;

  const handleSave = async () => {
    if (!name.trim()) { setLocalError('Group name is required.'); return; }
    setLocalError('');
    setSaving(true);
    try {
      const payload = {
        name:                  name.trim(),
        description,
        visibility,
        default_member_role:   visibility === 'public' ? defaultMemberRole : 'viewer',
        allow_member_invite:   allowMemberInvite,
        require_invite_approval: requireApproval,
        max_members:           maxMembers,
      };
      if (avatarFile) payload.avatar = avatarFile;

      const updated = await groupsAPI.updateGroup(group.id, payload);
      setAvatarFile(null);
      onSaved(updated);
    } catch (e) {
      const msg =
        e.response?.data?.name?.[0] ||
        e.response?.data?.detail ||
        'Failed to save settings.';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteInput !== group.name) {
      setLocalError('Group name does not match.');
      return;
    }
    setDeleting(true);
    try {
      await groupsAPI.deleteGroup(group.id);
      onDeleted();
    } catch (e) {
      setLocalError(e.response?.data?.detail || 'Failed to delete group.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="gsp">

      {localError && (
        <div className="gsp__error">
          <AlertTriangle size={15} /> {localError}
        </div>
      )}

      {/* ── Avatar ── */}
      <section className="gsp__section">
        <h3 className="gsp__section-title">Group Avatar</h3>
        <div className="gsp__avatar-row">
          <div className="gsp__avatar-preview">
            {avatarPreview
              ? <img src={avatarPreview} alt="Group avatar" />
              : <div className="gsp__avatar-placeholder">
                  {name.charAt(0).toUpperCase()}
                </div>
            }
          </div>
          <div className="gsp__avatar-actions">
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={14} /> Change avatar
            </button>
            {avatarPreview && (
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => { setAvatarPreview(null); setAvatarFile(null); }}
              >
                Remove
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
        </div>
      </section>

      <div className="gsp__divider" />

      {/* ── Basic info ── */}
      <section className="gsp__section">
        <h3 className="gsp__section-title">Basic Information</h3>

        <div className="gsp__field">
          <label className="gsp__label">Group Name *</label>
          <input
            className="gsp__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            placeholder="Group name"
          />
        </div>

        <div className="gsp__field">
          <label className="gsp__label">Description</label>
          <textarea
            className="gsp__textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this group about?"
          />
        </div>

        <div className="gsp__field">
          <label className="gsp__label">Max Members</label>
          <input
            className="gsp__input gsp__input--sm"
            type="number"
            min={1}
            max={500}
            value={maxMembers}
            onChange={(e) => setMaxMembers(Number(e.target.value))}
          />
          <span className="gsp__field-hint">Current: {group.members_count} active members</span>
        </div>
      </section>

      <div className="gsp__divider" />

      {/* ── Visibility ── */}
      <section className="gsp__section">
        <h3 className="gsp__section-title">Visibility</h3>
        <div className="gsp__option-cards">
          {VISIBILITY_OPTIONS.map(({ value, icon: Icon, label, desc }) => (
            <div
              key={value}
              className={`gsp__option-card ${visibility === value ? 'gsp__option-card--selected' : ''}`}
              onClick={() => handleVisibilityChange(value)}
              role="radio"
              aria-checked={visibility === value}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleVisibilityChange(value)}
            >
              <Icon size={17} className="gsp__option-icon" />
              <div className="gsp__option-body">
                <span className="gsp__option-label">{label}</span>
                <span className="gsp__option-desc">{desc}</span>
              </div>
              {visibility === value && <Check size={15} className="gsp__option-check" />}
            </div>
          ))}
        </div>
      </section>

      {/* ── Default member role (public only) ── */}
      {visibility === 'public' && (
        <>
          <div className="gsp__divider" />
          <section className="gsp__section">
            <h3 className="gsp__section-title">Default Role for New Members</h3>
            <p className="gsp__section-desc">
              Role automatically assigned when someone joins without an invitation.
            </p>
            <div className="gsp__option-cards gsp__option-cards--row">
              {DEFAULT_ROLE_OPTIONS.map(({ value, label, desc }) => (
                <div
                  key={value}
                  className={`gsp__option-card ${defaultMemberRole === value ? 'gsp__option-card--selected' : ''}`}
                  onClick={() => setDefaultMemberRole(value)}
                  role="radio"
                  aria-checked={defaultMemberRole === value}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setDefaultMemberRole(value)}
                >
                  <div className="gsp__option-body">
                    <span className="gsp__option-label">{label}</span>
                    <span className="gsp__option-desc">{desc}</span>
                  </div>
                  {defaultMemberRole === value && <Check size={15} className="gsp__option-check" />}
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <div className="gsp__divider" />

      {/* ── Permissions ── */}
      <section className="gsp__section">
        <h3 className="gsp__section-title">Permissions</h3>
        <div className="gsp__toggles">

          <label className="gsp__toggle-row">
            <div className="gsp__toggle-info">
              <span className="gsp__toggle-label">Allow editors to invite</span>
              <span className="gsp__toggle-desc">
                Editors (not just admins) can send invitations
              </span>
            </div>
            <div className="gsp__toggle-wrap">
              <input
                type="checkbox"
                className="gsp__toggle-input"
                checked={allowMemberInvite}
                onChange={(e) => setAllowMemberInvite(e.target.checked)}
              />
              <span className="gsp__toggle-slider" />
            </div>
          </label>

          {visibility === 'public' && (
            <label className="gsp__toggle-row">
              <div className="gsp__toggle-info">
                <span className="gsp__toggle-label">Require approval for join requests</span>
                <span className="gsp__toggle-desc">
                  Admins must approve before new members gain access
                </span>
              </div>
              <div className="gsp__toggle-wrap">
                <input
                  type="checkbox"
                  className="gsp__toggle-input"
                  checked={requireApproval}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                />
                <span className="gsp__toggle-slider" />
              </div>
            </label>
          )}

        </div>
      </section>

      {/* ── Save button ── */}
      <div className="gsp__save-row">
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving || !isDirty()}
        >
          {saving
            ? <><RefreshCw size={14} className="spinning" /> Saving…</>
            : <><Save size={14} /> Save Changes</>
          }
        </button>
        {!isDirty() && (
          <span className="gsp__no-changes">No unsaved changes</span>
        )}
      </div>

      {/* ── Danger zone (owner only) ── */}
      {isOwner && (
        <>
          <div className="gsp__divider" />
          <section className="gsp__section gsp__section--danger">
            <h3 className="gsp__section-title gsp__section-title--danger">
              <AlertTriangle size={16} /> Danger Zone
            </h3>

            {!confirmDelete ? (
              <div className="gsp__danger-row">
                <div>
                  <p className="gsp__danger-label">Delete this group</p>
                  <p className="gsp__danger-desc">
                    Permanently removes the group, all memberships, and shared resources.
                    This cannot be undone.
                  </p>
                </div>
                <button
                  className="btn btn--ghost btn--danger"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 size={14} /> Delete Group
                </button>
              </div>
            ) : (
              <div className="gsp__delete-confirm">
                <p className="gsp__delete-confirm-text">
                  Type <strong>{group.name}</strong> to confirm deletion:
                </p>
                <input
                  className="gsp__input gsp__input--danger"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder={group.name}
                  autoFocus
                />
                <div className="gsp__delete-confirm-actions">
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => { setConfirmDelete(false); setDeleteInput(''); setLocalError(''); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn--sm btn--ghost btn--danger"
                    onClick={handleDelete}
                    disabled={deleting || deleteInput !== group.name}
                  >
                    {deleting
                      ? <><RefreshCw size={13} className="spinning" /> Deleting…</>
                      : <><Trash2 size={13} /> Confirm Delete</>
                    }
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default GroupSettingsPanel;
